/**
 * Mr. Bubbles — the Brix chat mascot, reused as the face of the guided tour.
 *
 * The artwork lives in `public/brand/` as `mr-bubbles-<expression>.png`
 * (happy / helpful / excited / thinking). Until those files are dropped in,
 * every variant gracefully falls back to a °bx bubble emoji so nothing looks
 * broken. Same asset is injected into the driver.js popover header
 * (see TourProvider) — keep the path convention in sync.
 */
export type BubblesMood = 'happy' | 'helpful' | 'excited' | 'thinking';

export function bubblesSrc(mood: BubblesMood = 'happy'): string {
  return `/brand/mr-bubbles-${mood}.png`;
}

/** Full-body Mr. Bubbles (cap, bowtie, sneakers) — the chat mascot. */
export function bubblesFullSrc(): string {
  return '/brand/mr-bubbles-full.png';
}

function bubblesImg(src: string, size: number, className: string) {
  return (
    <img
      src={src}
      alt="Mr. Bubbles"
      width={size}
      height={size}
      className={`select-none object-contain ${className}`}
      style={{ width: size, height: size }}
      onError={(e) => {
        // No artwork yet → swap in a bubble glyph so the layout still reads.
        const img = e.currentTarget;
        if (img.dataset.fallback) return;
        img.dataset.fallback = '1';
        const span = document.createElement('span');
        span.textContent = '🫧';
        span.setAttribute('role', 'img');
        span.setAttribute('aria-label', 'Mr. Bubbles');
        span.style.cssText = `display:inline-flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;font-size:${Math.round(
          size * 0.6,
        )}px;`;
        img.replaceWith(span);
      }}
    />
  );
}

export function MrBubbles({
  mood = 'happy',
  size = 48,
  className = '',
}: {
  mood?: BubblesMood;
  size?: number;
  className?: string;
}) {
  return bubblesImg(bubblesSrc(mood), size, className);
}

/** Full-body Mr. Bubbles — use for the chat launcher + empty state. */
export function MrBubblesFull({ size = 56, className = '' }: { size?: number; className?: string }) {
  return bubblesImg(bubblesFullSrc(), size, className);
}
